import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import { AUTH_COOKIE_NAME, AuthCookieValue, CUST_GUID_COOKIE_NAME, MILLISECOND_MULTIPLIER, StorageToken } from 'core/constants';
import { Util } from '../util';
import { LocalStorage } from '../storage/local';
import { SessionStorage } from '../storage/session';

const ONE_MINUTE: number = 60;
const RESI_USER_TYPE: string = 'Residential';

@Injectable({
    providedIn: 'root'
})
export class CimaToken {
    public code: string;
    public accessToken: string;
    public userAccessToken: string;
    public refreshToken: string;
    public idToken: string;
    public decodedIdToken: JWT;
    public loginChange: Observable<boolean>;

    private loginChangeSubject: BehaviorSubject<boolean>;
    private expirationTimeWithOffset: number;

    private localStorage: LocalStorage;
    private sessionStorage: SessionStorage;

    constructor(localStorage: LocalStorage, sessionStorage: SessionStorage) {
        Object.assign(this, { localStorage, sessionStorage });

        this.loginChangeSubject = new BehaviorSubject(false);
        this.loginChange = this.loginChangeSubject.pipe(distinctUntilChanged());
        this.loadTokens();
    }

    public get tokens(): CimaTokens {
        return {
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            expirationTime: this.expirationTimeWithOffset,
            idToken: this.idToken
        };
    }

    public get isBusinessUser(): boolean {
        const idToken: string = this.idToken;

        return idToken && Util.decodeJWT(idToken) && Util.decodeJWT(idToken).user_type !== RESI_USER_TYPE;
    }

    public saveNewToken(token: string, expirationTimeInSeconds: number, refreshToken: string, idToken: string): void {
        this.code = ''; // whenever you set a new token, expire the existing code
        this.accessToken = token;
        this.refreshToken = refreshToken;
        this.idToken = idToken;
        this.decodedIdToken = Util.decodeJWT(idToken);

        const expirationTime: number = Date.now() + (expirationTimeInSeconds - ONE_MINUTE) * MILLISECOND_MULTIPLIER;

        // an effort to offset the multiple tabs from updating the same refresh token
        this.expirationTimeWithOffset = expirationTime - this.generateRandomOffset();
        this.localStorage.set(StorageToken.CIMA, JSON.stringify({
            accessToken: this.accessToken,
            refreshToken: this.refreshToken,
            idToken: this.idToken,
            expirationTime
        }));

        this.createCookie(AUTH_COOKIE_NAME, AuthCookieValue.SET);
        this.createCookie(CUST_GUID_COOKIE_NAME, this.decodedIdToken.sub);
        this.loginChangeSubject.next(true);
    }

    public logoutCleanup(): void {
        this.createCookie(AUTH_COOKIE_NAME, AuthCookieValue.UNSET);
        this.createCookie(CUST_GUID_COOKIE_NAME, '', -1);
        this.localStorage.destroy(StorageToken.CIMA);
        this.localStorage.destroy(StorageToken.USER_GRANT_TOKEN);
        this.sessionStorage.destroy(StorageToken.UUID);
    }

    public get isLoggedIn(): boolean {
        // check a token exist and the token has not expired
        return this.accessToken && !this.isExpired();
    }

    public checkForNewTokens(): boolean {
        const tokens: CimaTokens = this.localStorage.get<CimaTokens>(StorageToken.CIMA);
        if (tokens && tokens.accessToken && this.accessToken !== tokens.accessToken) {
            this.loadTokens();

            return true;
        }

        return false;
    }

    public isExpired(tokens?: CimaTokens): boolean {
        const expirationTime: number = tokens ? tokens.expirationTime : this.expirationTimeWithOffset;

        if (!expirationTime) {
            return true;
        }

        return expirationTime < Date.now();
    }

    public createCookie(name: string, value: string, days: number = 1): void {
        let cookie: string;
        const date: Date = new Date();
        const baseDomain: string = window.location.hostname.split('.').slice(-2).join('.');

        date.setTime(date.getTime() + ((days) * 864e5));
        const expires: string = date.toUTCString();
        cookie = `${encodeURIComponent(name)}=${value};expires=${expires};path=/`;

        if (baseDomain.includes('.')) {
            cookie += ';domain=' + baseDomain;
        }

        window.document.cookie = cookie;
    }

    private loadTokens(): void {
        const search: UrlQueries = Util.getObjectFromQueryString(window.location.search.slice(1));

        // first time coming back from CIMA with code
        if (search.code) {
            this.code = search.code;

            return;
        }

        // Otherwise get them from localstorage
        const tokens: CimaTokens = this.localStorage.get<CimaTokens>(StorageToken.CIMA);
        if (!this.isExpired(tokens)) {
            this.accessToken = tokens.accessToken;
            this.refreshToken = tokens.refreshToken;
            this.idToken = tokens.idToken;
            this.decodedIdToken = Util.decodeJWT(tokens.idToken);
            this.expirationTimeWithOffset = tokens.expirationTime - this.generateRandomOffset();

            this.loginChangeSubject.next(true);
        }
    }

    private generateRandomOffset(): number {
        // possible values are from 0-20 (in milliseconds)
        return Math.floor(Math.random() * 20) * MILLISECOND_MULTIPLIER;
    }
}
